import { Button, Dialog, DialogBody, DialogSurface, DialogTitle, Dropdown, makeStyles, Option } from "@fluentui/react-components";
import { faAlignCenter, faAlignLeft, faAlignRight, faBold, faItalic, faQuoteRight, faRedo, faUnderline, faUndo } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useRef, useState } from "react";
import { CommonInfo } from "../CommonData/Config";

type RXRTEViewProps = {
    theme?: string;
    isOpen: boolean;
    closeTextView: () => void;
    value: string;
    onChange?: (html: string) => void;
}

const useStyles = makeStyles({
    dropdown: {
        "& .fui-Dropdown__button": {
            height: "26px",
            minHeight: "26px",
            padding: "0 5px",
            borderRadius: "3px",
        },
    },
    iconButton: {
        width: "24px",
        height: "24px",
        minWidth: "24px",
        border: "0px",
        borderRadius: "3px",
    }
});

const blockFormats = [
    { label: "Normal text", value: "p" },
    { label: "Heading 1", value: "h1" },
    { label: "Heading 2", value: "h2" },
    { label: "Heading 3", value: "h3" },
    { label: "Quote", value: "blockquote" }
];

const fontFamilies = [
    { label: "Sans serif", value: "sans-serif" },
    { label: "Serif", value: "serif" },
    { label: "Wide", value: "Arial Black, sans-serif" },
    { label: "Narrow", value: "Arial Narrow, sans-serif" },
    { label: "Comic Sans MS", value: "'Comic Sans MS', cursive" },
    { label: "Courier New", value: "'Courier New', monospace" },
    { label: "Garamond", value: "Garamond, serif" },
    { label: "Georgia", value: "Georgia, serif" },
    { label: "Tahoma", value: "Tahoma, sans-serif" },
    { label: "Trebuchet MS", value: "'Trebuchet MS', sans-serif" },
    { label: "Verdana", value: "Verdana, sans-serif" }
];

const fontSizes = [
    "8", "9", "10", "11", "12",
    "14", "16", "18", "20",
    "24", "28", "32", "36", "48", "72"
]

const RXRTEView = ({
    theme = "dark",
    isOpen,
    closeTextView,
    value,
    onChange
}: RXRTEViewProps) => {
    const styles = useStyles();
    const [open, setOpen] = useState(true);
    const editorRef = useRef<HTMLDivElement>(null);
    const [html, setHtml] = useState(value);

    useEffect(() => {
        setOpen(isOpen);
    }, [isOpen]);

    const updateContent = () => {
        const content = editorRef.current?.innerHTML || "";
        setHtml(content);
        onChange?.(content);
    };

    const applyBlockFormat = (tag: string) => {
        document.execCommand("formatBlock", false, tag);
        updateContent();
    };

    const applyFontFamily = (font: string) => {
        wrapSelection("span", { fontFamily: font });
    };

    const applyFontSize = (size: string) => {
        wrapSelection("span", { fontSize: `${size}px` });
    };

    const applyAlignment = (align: "left" | "center" | "right") => {
        document.execCommand(`justify${align.charAt(0).toUpperCase() + align.slice(1)}`);
        updateContent();
    };

    const wrapSelection = (tag: string, style?: Partial<CSSStyleDeclaration>) => {
        const selection = window.getSelection();
        if (!selection || selection.rangeCount === 0) return;

        const range = selection.getRangeAt(0);
        const wrapper = document.createElement(tag);

        if (style) Object.assign(wrapper.style, style);

        wrapper.appendChild(range.extractContents());
        range.insertNode(wrapper);

        selection.removeAllRanges();
        updateContent();
    };

    return (
        <div>
            {open && (
                <div
                    aria-modal="true"
                    role="dialog"
                    style={{
                        position: "fixed",
                        backgroundColor: "rgba(0, 0, 0, 0.5)",
                        inset: "0",
                        display: "flex",
                        zIndex: 999,
                        alignItems: "center",
                        justifyContent: "center"
                    }}
                // onClick={closeTextView}
                >
                    <div
                        style={{
                            height: 500,
                            width: 800,
                            backgroundColor: "#1A1A1A",
                            border: "2px solid #525252",
                            borderRadius: "4px",
                            padding: "10px",
                            gap: "10px",
                            display: "flex",
                            flexDirection: "row"
                        }}
                    >
                        <img src={`${CommonInfo.folderName}${theme === 'dark' ? '/note/light.svg' : '/note/dark.svg'}`} style={{ width: "30px", height: "30px" }} />
                        <div style={{ width: "740px", display: "flex", flexDirection: "column", gap: "10px" }}>
                            <div style={{ height: "30px", display: "flex", flexDirection: "row", alignItems: "center", paddingRight: "5px", paddingLeft: "5px", gap: "10px" }}>
                                <div style={{ width: 100 }}>
                                    <Dropdown
                                        size="small"
                                        className={styles.dropdown}
                                        placeholder="Normal text"
                                        onOptionSelect={(e, d) =>
                                            applyBlockFormat(d.optionValue as string)
                                        }
                                    >
                                        {blockFormats.map((b) => (
                                            <Option key={b.value} value={b.value}>
                                                {b.label}
                                            </Option>
                                        ))}
                                    </Dropdown>
                                </div>
                                <div style={{ width: 130 }}>
                                    <Dropdown
                                        size="small"
                                        className={styles.dropdown}
                                        placeholder="Font"
                                        onOptionSelect={(e, d) =>
                                            applyFontFamily(d.optionValue as string)
                                        }
                                    >
                                        {fontFamilies.map((font) => (
                                            <Option
                                                key={font.value}
                                                value={font.value}
                                                style={{ fontFamily: font.value }}
                                            >
                                                {font.label}
                                            </Option>
                                        ))}
                                    </Dropdown>
                                </div>
                                <div style={{ width: 50 }}>
                                    <Dropdown
                                        size="small"
                                        className={styles.dropdown}
                                        placeholder="12"
                                        onOptionSelect={(e, d) =>
                                            applyFontSize(d.optionValue as string)
                                        }
                                    >
                                        {fontSizes.map((size) => (
                                            <Option key={size} value={size}>
                                                {size}
                                            </Option>
                                        ))}
                                    </Dropdown>
                                </div>

                                <Button className={styles.iconButton} icon={<FontAwesomeIcon icon={faBold} size={"xs"} />} onClick={() => wrapSelection("b")} />
                                <Button className={styles.iconButton} icon={<FontAwesomeIcon icon={faItalic} size={"xs"} />} onClick={() => wrapSelection("i")} />
                                <Button className={styles.iconButton} icon={<FontAwesomeIcon icon={faUnderline} size={"xs"} />} onClick={() => wrapSelection("u")} />
                                <Button className={styles.iconButton} icon={<FontAwesomeIcon icon={faAlignLeft} size={"xs"} />} onClick={() => applyAlignment("left")} />
                                <Button className={styles.iconButton} icon={<FontAwesomeIcon icon={faAlignCenter} size={"xs"} />} onClick={() => applyAlignment("center")} />
                                <Button className={styles.iconButton} icon={<FontAwesomeIcon icon={faAlignRight} size={"xs"} />} onClick={() => applyAlignment("right")} />
                                <Button className={styles.iconButton} icon={<FontAwesomeIcon icon={faQuoteRight} size={"xs"} />} onClick={() => wrapSelection("blockquote")} />
                                <Button className={styles.iconButton} icon={<FontAwesomeIcon icon={faUndo} size={"xs"} />} onClick={() => document.execCommand("undo")} />
                                <Button className={styles.iconButton} icon={<FontAwesomeIcon icon={faRedo} size={"xs"} />} onClick={() => document.execCommand("redo")} />

                                <Button shape="circular" style={{ marginRight: 0, marginLeft: 20 }} onClick={() => closeTextView()}>Close</Button>
                            </div>
                            <div
                                ref={editorRef}
                                contentEditable
                                suppressContentEditableWarning
                                dangerouslySetInnerHTML={{ __html: html ?? "" }}
                                onInput={(e) => {
                                    const updatedHtml = (e.currentTarget as HTMLDivElement).innerHTML;
                                    onChange?.(updatedHtml);
                                }}
                                style={{ borderRadius: "3px", border: "1px solid #525252", padding: "10px", flex: 1, color: theme === "dark" ? "#fff" : "#1A1A1A" }}
                            />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default RXRTEView;